import { useEffect, type RefObject } from 'react';
import gsap from 'gsap';

const WINDOW_MS = 5 * 60 * 1000;
const TICK_MIN_MS = 1_000;
const TICK_MAX_MS = 3_000;
const STEP_MIN = 1;
const STEP_MAX = 6;
const COUNT_MIN_S = 0.4;
const COUNT_MAX_S = 0.8;
const REFRESH_URL = '/api/sleep-stats/total-nights/';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function formatNightsCount(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function randomInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function countDurationS(step: number): number {
  if (step <= 1) return COUNT_MIN_S;
  return COUNT_MIN_S + ((step - 1) * (COUNT_MAX_S - COUNT_MIN_S)) / (STEP_MAX - 1);
}

function parseRefreshPayload(json: unknown): { nights: number; nightsPer5m: number } | null {
  if (!json || typeof json !== 'object') return null;
  const d = json as Record<string, unknown>;
  if (typeof d.nights !== 'number' || !Number.isFinite(d.nights) || d.nights <= 0) {
    return null;
  }
  if (
    typeof d.nightsPer5m !== 'number' ||
    !Number.isFinite(d.nightsPer5m) ||
    d.nightsPer5m < 0
  ) {
    return null;
  }
  return {
    nights: Math.floor(d.nights),
    nightsPer5m: Math.round(d.nightsPer5m),
  };
}

/**
 * Live nights ticker: random +1–6 counted through integers every 1–3s.
 * Writes textContent on `countRef` so the globe does not re-render.
 */
export function useLiveNightsTick(
  countRef: RefObject<HTMLElement | null>,
  triggerRef: RefObject<HTMLElement | null>,
  nights: number,
  nightsPer5m: number,
): void {
  useEffect(() => {
    const countEl = countRef.current;
    const triggerEl = triggerRef.current;
    if (!countEl || !triggerEl) return;
    if (window.matchMedia(REDUCED_MOTION_QUERY).matches) return;

    const state = {
      value: nights,
      remaining: Math.max(0, nightsPer5m),
      nightsPer5m: Math.max(0, nightsPer5m),
    };
    let cancelled = false;
    let tickTimer: number | undefined;
    let refreshTimer: number | undefined;
    let tween: gsap.core.Tween | undefined;
    let started = false;

    const write = () => {
      countEl.textContent = formatNightsCount(state.value);
    };

    const killTween = () => {
      tween?.kill();
      tween = undefined;
    };

    const countTo = (target: number, duration: number, onComplete: () => void) => {
      killTween();
      const from = Math.round(state.value);
      if (target <= from) {
        state.value = from;
        write();
        onComplete();
        return;
      }
      state.value = from;
      tween = gsap.to(state, {
        value: target,
        duration,
        ease: 'none',
        snap: { value: 1 },
        onUpdate: write,
        onComplete: () => {
          state.value = target;
          write();
          onComplete();
        },
      });
    };

    const scheduleTick = () => {
      if (cancelled || state.remaining <= 0) return;
      window.clearTimeout(tickTimer);
      tickTimer = window.setTimeout(() => {
        if (cancelled || state.remaining <= 0) return;
        const step = Math.min(randomInt(STEP_MIN, STEP_MAX), state.remaining);
        state.remaining -= step;
        countTo(Math.round(state.value) + step, countDurationS(step), scheduleTick);
      }, randomBetween(TICK_MIN_MS, TICK_MAX_MS));
    };

    const refresh = async () => {
      window.clearTimeout(tickTimer);
      killTween();
      try {
        const res = await fetch(REFRESH_URL, {
          headers: { accept: 'application/json' },
        });
        if (!res.ok) throw new Error('refresh failed');
        const parsed = parseRefreshPayload(await res.json());
        if (!parsed || cancelled) throw new Error('bad payload');
        state.nightsPer5m = parsed.nightsPer5m;
        state.remaining = parsed.nightsPer5m;
        const current = Math.round(state.value);
        if (parsed.nights > current) {
          const gap = parsed.nights - current;
          const duration = Math.min(1.2, Math.max(COUNT_MIN_S, gap * 0.08));
          countTo(parsed.nights, duration, scheduleTick);
          return;
        }
        scheduleTick();
      } catch {
        if (cancelled) return;
        state.remaining = state.nightsPer5m;
        scheduleTick();
      }
    };

    const armRefresh = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        void refresh().finally(() => {
          if (!cancelled) armRefresh();
        });
      }, WINDOW_MS);
    };

    const start = () => {
      if (cancelled || started) return;
      started = true;
      write();
      scheduleTick();
      armRefresh();
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          start();
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px' },
    );
    io.observe(triggerEl);

    return () => {
      cancelled = true;
      io.disconnect();
      window.clearTimeout(tickTimer);
      window.clearTimeout(refreshTimer);
      killTween();
    };
  }, [countRef, triggerRef, nights, nightsPer5m]);
}
